import { useEffect, useState } from "react";
import { Download, Share2, X } from "lucide-react";

function isMobileDevice() {
  const userAgent = navigator.userAgent || "";
  return navigator.userAgentData?.mobile === true ||
    /android|iphone|ipad|ipod|mobile/i.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isInstalled() {
  return window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
}

export default function PwaInstallPrompt() {
  const [mobile, setMobile] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const isAppleMobile = /iphone|ipad|ipod/i.test(navigator.userAgent || "") ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  useEffect(() => {
    setMobile(isMobileDevice());
    setInstalled(isInstalled());

    if ("serviceWorker" in navigator) {
      if (process.env.NODE_ENV === "production") {
        navigator.serviceWorker.register("/sw.js").catch(() => {});
      } else {
        navigator.serviceWorker.getRegistrations().then(async (registrations) => {
          const appRegistrations = registrations.filter((registration) => {
            const scriptUrl = registration.active?.scriptURL || registration.waiting?.scriptURL || registration.installing?.scriptURL;
            return scriptUrl && new URL(scriptUrl).pathname === "/sw.js";
          });
          if (!appRegistrations.length) return;
          await Promise.all(appRegistrations.map((registration) => registration.unregister()));
          if ("caches" in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.filter((name) => /^(egs-field-|egs-app-)/.test(name)).map((name) => caches.delete(name)));
          }
          const reloadKey = "egs-dev-worker-removed";
          if (navigator.serviceWorker.controller && !sessionStorage.getItem(reloadKey)) {
            sessionStorage.setItem(reloadKey, "1");
            window.location.reload();
          } else if (!navigator.serviceWorker.controller) {
            sessionStorage.removeItem(reloadKey);
          }
        }).catch(() => {});
      }
    }

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
      setShowInstructions(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!installEvent) {
      setShowInstructions(true);
      return;
    }
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice?.outcome === "accepted") setInstalled(true);
    setInstallEvent(null);
  };

  if (!mobile || installed || dismissed) return null;

  return (
    <>
      <aside className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+5rem)] z-[80] mx-auto flex max-w-lg items-center gap-3 rounded-xl border border-emerald-900/15 bg-white p-3 text-slate-900 shadow-[0_12px_36px_rgba(10,20,15,0.22)]" data-testid="pwa-install-prompt" aria-label="Install EGS Business Suite">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white"><img src="/branding/egs-logo.jpg" alt="" className="h-full w-full object-contain" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Install EGS Suite</p>
          <p className="text-xs text-slate-600">Add the app to your home screen.</p>
        </div>
        <button type="button" onClick={install} className="flex min-h-10 shrink-0 items-center gap-1.5 rounded-lg bg-emerald-900 px-3 text-sm font-semibold text-white" data-testid="pwa-install-button"><Download className="h-4 w-4" />Install</button>
        <button type="button" onClick={() => setDismissed(true)} aria-label="Dismiss install prompt" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid="pwa-install-dismiss"><X className="h-4 w-4" /></button>
      </aside>
      {showInstructions && <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/50 p-3 pb-[max(12px,env(safe-area-inset-bottom))] sm:items-center" role="presentation" onClick={() => setShowInstructions(false)}>
        <section className="w-full max-w-sm rounded-xl bg-white p-5 text-slate-900 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="pwa-install-title" onClick={(event) => event.stopPropagation()} data-testid="pwa-install-instructions">
          <div className="flex items-start justify-between gap-4">
            <div><h2 id="pwa-install-title" className="text-base font-semibold">Add EGS Suite to your home screen</h2><p className="mt-2 text-sm text-slate-600">{isAppleMobile ? "Tap Share, then choose Add to Home Screen." : "Open your browser menu, then choose Install app or Add to Home screen."}</p></div>
            <button type="button" aria-label="Close installation instructions" onClick={() => setShowInstructions(false)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600"><Share2 className="h-4 w-4 shrink-0 text-emerald-900" />{isAppleMobile ? "Use the Share button in Safari." : "Look for Install app or Add to Home screen in the browser menu."}</div>
          <button type="button" onClick={() => setShowInstructions(false)} className="mt-4 h-11 w-full rounded-lg bg-emerald-900 text-sm font-semibold text-white">Got it</button>
        </section>
      </div>}
    </>
  );
}